import { Params } from '@sentry/react/types/types';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../../actions/framework';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { z } from 'zod/v3';
import { err, ok, Result } from 'neverthrow';
import { ExpressionColumnModel } from '../expression-column.model';
import { toast } from '@cotera/client/app/components/ui';
import { Constant, EraQL, Expression } from '@cotera/era';
import { Assert } from '@cotera/utilities';

export class RunPreviewExpressionColumnAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Run Column Preview';
  inputSchema = z.object({});
  shortcut = ['ctrl', 'enter'];
  group = COLUMN_ACTION_GROUP;
  icon = 'play';

  applicable(context: ApplicableContext): boolean {
    return (
      context.hasTargetedModelOfType(ExpressionColumnModel) &&
      !!context
        .targetedModelOfType(ExpressionColumnModel)
        ?.datasetRel.snapshot()
        ?.isOk()
    );
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(ExpressionColumnModel);

    Assert.assert(model !== null);

    const name = model.currentNameInGraph.snapshot();
    // Relation of the dataset
    const rel = model.datasetRel.snapshot()!.map((item) => item.rel);

    Assert.assert(rel.isOk());

    const userInputEraQL = model?.formState.snapshot().eraQLEditorValue;

    const makeSelectData = (): Result<
      { deps: string[]; userInputExpr: Expression },
      string[]
    > => {
      if (userInputEraQL !== undefined && userInputEraQL.length > 0) {
        const userInputExpr = EraQL.parseExpr(userInputEraQL, {
          attributes: rel.value.attributes,
        });

        if (userInputExpr.isErr()) {
          //TODO
          return err([]);
        }

        const deps = Object.keys(userInputExpr.value.attrReqs.from ?? {});

        return ok({
          deps,
          userInputExpr: userInputExpr.value,
        });
      }

      return ok({
        deps: [],
        userInputExpr: Constant('test'),
      });
    };

    const dataRes = makeSelectData();

    if (dataRes.isErr()) {
      toast.error('Invalid prompt');
      //TODO set errors
      return err([]);
    }

    const { deps, userInputExpr } = dataRes.value;

    toast.info(
      `Running preview for ${name} with source eraQL: ${userInputEraQL}`
    );

    model.preview.set(
      rel.value.select(
        (t) => ({
          ...deps
            .map((d) => ({ [d]: t.attr(d) }))
            .reduce((acc, curr) => ({ ...acc, ...curr }), {}),
          [name]: userInputExpr,
        }),
        {
          distinct: true,
          limit: 300,
        }
      )
    );

    return ok({});
  }
}
